import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  LogOut,
  RefreshCw,
  Mail,
  MessageCircle,
  Phone,
  CalendarDays,
  Filter,
  Trash2,
  CheckCircle2,
  XCircle,
  Clock,
  CircleCheck,
  Loader2,
  ExternalLink,
} from "lucide-react";
import Logo from "@/components/site/Logo";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pending: { label: "À confirmer", color: "#F59E0B", bg: "#FEF3C7", icon: Clock },
  confirmed: { label: "Confirmée", color: "#10B981", bg: "#D1FAE5", icon: CheckCircle2 },
  completed: { label: "Terminée", color: "#6366F1", bg: "#E0E7FF", icon: CircleCheck },
  cancelled: { label: "Annulée", color: "#EF4444", bg: "#FEE2E2", icon: XCircle },
};

const FILTERS = [
  { id: "all", label: "Toutes" },
  { id: "upcoming", label: "À venir" },
  { id: "pending", label: "À confirmer" },
  { id: "confirmed", label: "Confirmées" },
  { id: "past", label: "Passées" },
];

function formatDate(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("fr-FR", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatDateTime(iso) {
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function getInitials(name) {
  if (!name) return "?";
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.pending;
  const Icon = meta.icon;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold"
      style={{ background: meta.bg, color: meta.color }}
    >
      <Icon size={12} />
      {meta.label}
    </span>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("upcoming");
  const [selected, setSelected] = useState(null);

  const token = localStorage.getItem("numedia_admin_token");

  const authHeaders = useMemo(
    () => ({ headers: { Authorization: token ? `Bearer ${token}` : "" } }),
    [token]
  );

  const logout = useCallback(() => {
    localStorage.removeItem("numedia_admin_token");
    localStorage.removeItem("numedia_admin_pw"); // legacy cleanup
    navigate("/admin/login", { replace: true });
  }, [navigate]);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/bookings`, authHeaders);
      setBookings(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expirée, reconnexion nécessaire.");
        logout();
      } else {
        toast.error("Impossible de charger les réservations.");
      }
    } finally {
      setLoading(false);
    }
  }, [authHeaders, logout]);

  useEffect(() => {
    if (!token) {
      navigate("/admin/login", { replace: true });
      return;
    }
    fetchBookings();
  }, [token, navigate, fetchBookings]);

  const updateStatus = useCallback(
    async (booking, newStatus) => {
      try {
        await axios.patch(
          `${API}/bookings/${booking.id}/status`,
          { status: newStatus },
          authHeaders
        );
        setBookings((prev) =>
          prev.map((b) => (b.id === booking.id ? { ...b, status: newStatus } : b))
        );
        setSelected((prev) =>
          prev?.id === booking.id ? { ...prev, status: newStatus } : prev
        );
        toast.success(`Statut mis à jour : ${STATUS_META[newStatus].label}`);
      } catch (err) {
        toast.error("Erreur lors de la mise à jour.");
      }
    },
    [authHeaders]
  );

  const deleteBooking = useCallback(
    async (booking) => {
      if (!window.confirm(`Supprimer définitivement la réservation de ${booking.name} ?`)) return;
      try {
        await axios.delete(`${API}/bookings/${booking.id}`, authHeaders);
        setBookings((prev) => prev.filter((b) => b.id !== booking.id));
        setSelected((prev) => (prev?.id === booking.id ? null : prev));
        toast.success("Réservation supprimée.");
      } catch (err) {
        toast.error("Erreur lors de la suppression.");
      }
    },
    [authHeaders]
  );

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const filtered = useMemo(() => {
    const list = [...bookings];
    switch (filter) {
      case "upcoming":
        return list
          .filter((b) => b.date >= today && b.status !== "cancelled")
          .sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time));
      case "past":
        return list
          .filter((b) => b.date < today || b.status === "completed")
          .sort((a, b) => b.date.localeCompare(a.date) || b.time.localeCompare(a.time));
      case "pending":
        return list.filter((b) => b.status === "pending");
      case "confirmed":
        return list.filter((b) => b.status === "confirmed");
      default:
        return list;
    }
  }, [bookings, filter, today]);

  const stats = useMemo(() => {
    return {
      total: bookings.length,
      upcoming: bookings.filter((b) => b.date >= today && b.status !== "cancelled").length,
      pending: bookings.filter((b) => b.status === "pending").length,
      thisWeek: bookings.filter((b) => {
        const d = new Date(b.date);
        const now = new Date();
        const weekAhead = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        return d >= now && d <= weekAhead && b.status !== "cancelled";
      }).length,
    };
  }, [bookings, today]);

  return (
    <main
      data-testid="admin-dashboard"
      className="min-h-screen bg-[#FAFAFA] text-[#0B1027]"
    >
      {/* Top bar */}
      <header className="border-b border-[#0B1027]/8 bg-white sticky top-0 z-30">
        <div className="mx-auto max-w-7xl px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2.5" aria-label="Numedia">
            <Logo size={32} />
            <div className="hidden sm:flex flex-col leading-none">
              <span className="font-brand text-base font-medium tracking-tight uppercase">
                Numedia
              </span>
              <span className="text-[9px] uppercase tracking-[0.22em] text-aurora font-bold mt-0.5">
                Cockpit
              </span>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchBookings}
              disabled={loading}
              data-testid="admin-refresh"
              className="h-9 w-9 rounded-full bg-[#F3F0FF] text-[#A855F7] flex items-center justify-center hover:bg-[#E9E1FF] transition-colors disabled:opacity-50"
              aria-label="Rafraîchir"
            >
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="h-9 px-4 rounded-full text-xs font-medium text-[#0B1027]/70 hover:text-[#0B1027] hover:bg-[#0B1027]/5 transition-colors inline-flex items-center gap-1.5"
            >
              <LogOut size={14} />
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 md:px-8 py-8 md:py-12">
        {/* Headline + Stats */}
        <div className="mb-8">
          <span className="eyebrow text-aurora">— Réservations</span>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight font-medium mt-3 leading-tight">
            Bonjour David,{" "}
            <em className="not-italic italic font-light text-aurora">
              voici votre semaine.
            </em>
          </h1>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8">
          <StatCard label="À venir" value={stats.upcoming} accent="#A855F7" />
          <StatCard label="Cette semaine" value={stats.thisWeek} accent="#EC4899" />
          <StatCard label="À confirmer" value={stats.pending} accent="#F59E0B" />
          <StatCard label="Total" value={stats.total} accent="#6366F1" />
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-6 -mx-1 px-1">
          <Filter size={14} className="text-[#0B1027]/40 shrink-0" />
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`filter-${f.id}`}
              className={`h-9 px-4 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                filter === f.id
                  ? "btn-aurora text-white shadow-tactile"
                  : "bg-white text-[#0B1027]/70 border border-[#0B1027]/10 hover:border-[#A855F7]/40"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Bookings list */}
        {loading ? (
          <div className="bg-white rounded-2xl border border-[#0B1027]/8 p-12 flex items-center justify-center text-[#0B1027]/60">
            <Loader2 size={20} className="animate-spin mr-2" />
            Chargement…
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-[#0B1027]/8 p-12 text-center">
            <CalendarDays size={32} className="mx-auto text-[#0B1027]/30 mb-3" />
            <p className="text-[#0B1027]/60 text-sm">
              Aucune réservation pour ce filtre.
            </p>
          </div>
        ) : (
          <div className="space-y-2.5" data-testid="bookings-list">
            {filtered.map((b) => (
              <button
                key={b.id}
                onClick={() => setSelected(b)}
                data-testid={`booking-row-${b.id}`}
                className="w-full text-left bg-white rounded-2xl border border-[#0B1027]/8 hover:border-[#A855F7]/40 hover:shadow-tactile transition-all p-4 md:p-5 flex items-center gap-4"
              >
                <div
                  className="h-12 w-12 shrink-0 rounded-full flex items-center justify-center text-white font-display text-sm font-medium"
                  style={{
                    background:
                      "linear-gradient(135deg, #6366F1, #A855F7, #EC4899)",
                  }}
                >
                  {getInitials(b.name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-[#0B1027] truncate">{b.name}</span>
                    <StatusBadge status={b.status} />
                  </div>
                  <div className="text-xs text-[#0B1027]/60 mt-1 flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1">
                      <CalendarDays size={12} />
                      {formatDate(b.date)} · {b.time}
                    </span>
                    <span className="hidden sm:inline">·</span>
                    <span className="truncate">{b.service_label}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Detail drawer */}
      {selected && (
        <BookingDetail
          booking={selected}
          onClose={() => setSelected(null)}
          onStatusChange={(s) => updateStatus(selected, s)}
          onDelete={() => deleteBooking(selected)}
        />
      )}
    </main>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div
      className="bg-white rounded-2xl border border-[#0B1027]/8 p-4 md:p-5"
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#0B1027]/55 font-bold">
        {label}
      </div>
      <div
        className="font-display text-3xl md:text-4xl font-medium leading-none mt-2"
        style={{ color: accent }}
      >
        {value}
      </div>
    </div>
  );
}

function BookingDetail({ booking, onClose, onStatusChange, onDelete }) {
  const meta = STATUS_META[booking.status] || STATUS_META.pending;
  return (
    <div
      className="fixed inset-0 z-40 flex items-end md:items-center justify-center bg-[#0B1027]/40 backdrop-blur-sm"
      onClick={onClose}
      data-testid="booking-detail-drawer"
    >
      <div
        className="bg-white w-full md:max-w-2xl md:m-6 rounded-t-3xl md:rounded-3xl shadow-tactile-lg max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="p-6 md:p-8 text-white relative"
          style={{
            background:
              "linear-gradient(135deg, #6366F1 0%, #A855F7 50%, #EC4899 100%)",
          }}
        >
          <button
            onClick={onClose}
            data-testid="booking-detail-close"
            aria-label="Fermer"
            className="absolute top-4 right-4 h-9 w-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-white transition-colors"
          >
            ✕
          </button>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold opacity-80">
            Réservation
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight mt-2 leading-tight">
            {booking.name}
          </h2>
          <div className="mt-3 inline-flex">
            <StatusBadge status={booking.status} />
          </div>
        </div>

        {/* Body */}
        <div className="p-6 md:p-8 space-y-6">
          <Section title="Rendez-vous">
            <Row label="Date" value={formatDate(booking.date)} />
            <Row label="Heure" value={booking.time} />
            <Row label="Service" value={booking.service_label} />
            <Row label="Reçu le" value={formatDateTime(booking.created_at)} />
          </Section>

          <Section title="Contact">
            <Row label="Email" value={booking.email} />
            {booking.phone && <Row label="Téléphone" value={booking.phone} />}
            {booking.company && <Row label="Activité / Société" value={booking.company} />}
          </Section>

          {booking.message && (
            <Section title="Message">
              <div className="bg-[#F3F0FF] rounded-2xl p-4 text-sm text-[#0B1027] leading-relaxed whitespace-pre-wrap">
                {booking.message}
              </div>
            </Section>
          )}

          {/* Quick contact actions */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <a
              href={`mailto:${booking.email}`}
              data-testid="action-email"
              className="btn-outline-aurora inline-flex items-center justify-center gap-2 rounded-full h-12 px-4 text-sm font-medium text-[#0B1027] border border-[#0B1027]/15 hover:text-[#A855F7]"
            >
              <Mail size={15} />
              Email
            </a>
            {booking.phone && (
              <>
                <a
                  href={`tel:${booking.phone.replace(/\s+/g, "")}`}
                  data-testid="action-phone"
                  className="btn-outline-aurora inline-flex items-center justify-center gap-2 rounded-full h-12 px-4 text-sm font-medium text-[#0B1027] border border-[#0B1027]/15 hover:text-[#A855F7]"
                >
                  <Phone size={15} />
                  Appeler
                </a>
                <a
                  href={`https://wa.me/${booking.phone.replace(/[^\d]/g, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="action-whatsapp"
                  className="btn-outline-aurora inline-flex items-center justify-center gap-2 rounded-full h-12 px-4 text-sm font-medium text-[#0B1027] border border-[#0B1027]/15 hover:text-[#A855F7]"
                >
                  <MessageCircle size={15} />
                  WhatsApp
                  <ExternalLink size={12} />
                </a>
              </>
            )}
          </div>

          {/* Status actions */}
          <Section title="Changer le statut">
            <div className="flex flex-wrap gap-2">
              {Object.entries(STATUS_META).map(([key, m]) => {
                const Icon = m.icon;
                const active = booking.status === key;
                return (
                  <button
                    key={key}
                    onClick={() => !active && onStatusChange(key)}
                    disabled={active}
                    data-testid={`status-${key}`}
                    className={`inline-flex items-center gap-2 px-4 h-10 rounded-full text-xs font-semibold transition-all ${
                      active
                        ? "opacity-60 cursor-not-allowed"
                        : "hover:scale-[1.02] hover:shadow-tactile"
                    }`}
                    style={{
                      background: m.bg,
                      color: m.color,
                      border: `1px solid ${m.color}40`,
                    }}
                  >
                    <Icon size={13} />
                    {m.label}
                  </button>
                );
              })}
            </div>
          </Section>

          {/* Danger zone */}
          <div className="pt-4 border-t border-[#0B1027]/10">
            <button
              onClick={onDelete}
              data-testid="action-delete"
              className="inline-flex items-center gap-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 px-3 py-2 rounded-lg transition-colors"
            >
              <Trash2 size={14} />
              Supprimer définitivement
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#0B1027]/55 font-bold mb-3">
        {title}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start gap-4 text-sm">
      <div className="w-28 shrink-0 text-[#0B1027]/55">{label}</div>
      <div className="flex-1 text-[#0B1027] break-words">{value}</div>
    </div>
  );
}
